import "./addressSearch.scss";
interface AddressSearchProps {
    apiKey: string;
    placeholder?: string;
    lookupAddressUrl: string;
    suggestionDelay?: number;
    validateAddressUrl: string;
}

export class AddressSearch {
    private selectCallback: (data, searchInput) => void = () => { };
    private openCallback: (searchInput) => void = () => { };

    private apiKey: string;
    private suggestionDelay = 300;
    private placeholder: string;
    private lookupAddressUrl: string;
    private addressSuggestionMenuClass = "address-suggestions"; 
    private addressSuggestionContainerClass = "address-search-wrapper";
    private searchInput: HTMLInputElement;
    private processSuggestionRequest: (query: string) => void;

    constructor(selector: string, options: AddressSearchProps) {
        // set options
        this.apiKey = options.apiKey;
        this.placeholder = options.placeholder || "Search...";
        this.lookupAddressUrl = options.lookupAddressUrl;
        this.suggestionDelay = options.suggestionDelay || this.suggestionDelay;

        // Run the suggestion ajax calls through debounce method
        this.processSuggestionRequest = this.debounce(
            (query) => this.getSuggestions(query),
            this.suggestionDelay
        );

        this.setSearch(selector);
    }

    private setSearch(selector: string) {
        const searchFields = document.querySelectorAll<HTMLInputElement>(selector);
        searchFields.forEach((element: HTMLInputElement) => {
            element.addEventListener("keydown", this.handleKeyDown);
            element.addEventListener("input", this.handleInput);
            element.addEventListener("click", (e) => { e.preventDefault(); });
            this.buildAddressMenu(element);
            this.buildAutoCompleteBypass(element);
        });
    }

    private debounce(func, timeout) {
        let timer;
        return (...args) => {
            clearTimeout(timer);
            timer = setTimeout(() => { func.apply(this, args); }, timeout);
        };
    }

    private handleKeyDown = (event: KeyboardEvent) => {
        // We are preventing a form submission when hitting enter in the search field.
        if (event.key === "Enter") {
            event.preventDefault();
        }
    }

    private handleKeyUp = (event: KeyboardEvent) => {
        const searchInput = event.target as HTMLInputElement; 
        this.searchInput = searchInput;
        const searchQuery = searchInput.value;

        if (event.key === "ArrowDown") {
            this.getMenu().querySelector('li').focus();
        } else {
            if (searchQuery) {
                this.showAddressMenu();
                this.searchOpen();
                this.processSuggestionRequest(searchInput.value);
            } else {
                this.hideAddressMenu();
            }
        }
    }

    private handleInput = (event: Event) => { // Updated function signature
        const searchInput = event.target as HTMLInputElement;
        this.searchInput = searchInput;
        const searchQuery = searchInput.value;

        if (searchQuery) {
            this.showAddressMenu();
            this.searchOpen();
            this.processSuggestionRequest(searchInput.value);
        } else {
            this.hideAddressMenu();
        }
    }

    private buildAutoCompleteBypass = (searchInput) => {
        const hiddenInput = document.createElement("input");
        hiddenInput.style.display = "none";
        hiddenInput.autocomplete = "address-hidden-field";
        const wrapper = searchInput.closest(".address-search-wrapper");
        wrapper.insertBefore(hiddenInput, wrapper.firstChild);
    }

    private buildAddressMenu = (searchInput) => {
        const menu = document.createElement("ul");
        menu.classList.add(this.addressSuggestionMenuClass);
        searchInput.insertAdjacentElement('afterend', menu);
    }

    private showAddressMenu = () => {
        const menu = this.getMenu();
        menu.style.display = "block";
    }

    private hideAddressMenu = () => {
        const menu = this.getMenu();
        menu.style.display = "none";
    }

    private buildAddress(suggestion): string {
        const additionalEntries = suggestion.entries > 1 ? `<span class='additional-entries-found'>+ ${suggestion.entries} more <i class='fas fa-chevron-right'></i></span>` : "";
        let address = `${suggestion.street_line} ${suggestion.secondary} ${suggestion.city}, ${suggestion.state} ${suggestion.zipcode} ${additionalEntries}`;

        const inputAddress = this.searchInput.value;
        for (let i = 0; i < address.length; i++) {
            const theLettersMatch = typeof inputAddress[i] == "undefined" || address[i].toLowerCase() !== inputAddress[i].toLowerCase();

            if (theLettersMatch) {
                address = [address.slice(0, i), "<strong>", address.slice(i)].join("");
                break;
            }
        }

        return address;
    }

    private getMenu = () => {
        const parentWrapperSelector = "." + this.addressSuggestionContainerClass;
        const menuSelector = "." + this.addressSuggestionMenuClass;
        const parentWrapper = this.searchInput.closest(parentWrapperSelector);
        const menu = parentWrapper.querySelector(menuSelector) as HTMLElement;
        return menu;
    }

    private displaySuggestions = (suggestions) => {
        const menu = this.getMenu();
        menu.innerHTML = "";
        suggestions.forEach((suggestion) => {
            const listItem = document.createElement('li');
            const div = document.createElement('div');
            div.innerHTML = this.buildAddress(suggestion);
            div.addEventListener("click", this.handleSelection);
            const { street_line, secondary, city, state, zipcode, entries } = suggestion;
            div.dataset.address1 = street_line;
            div.dataset.address2 = secondary;
            div.dataset.city = city;
            div.dataset.state = state;
            div.dataset.zip = zipcode;
            div.dataset.entries = entries;
            listItem.appendChild(div);
            menu.appendChild(listItem);
        });
    }

    private handleSelection = (e) => {
        const selection = e.currentTarget as HTMLElement;
        const address = { ...selection.dataset }
        const { address1, address2, entries, city, state, zip } = address;
        const numOfUnits = parseInt(address.entries);

        if (numOfUnits > 1) {
            // get all the sub units
            const addressInput = this.searchInput as HTMLInputElement;
            addressInput.value = address1 + " ";
            const selected = `${address1} ${address2} (${entries}) ${city} ${state} ${zip}`;
            this.getSuggestions(address1, selected);
        } else {
            // Single address without sub units
            this.hideAddressMenu();
            this.selectSuccess(address);
        }
    }

    private getSuggestions(query, selected?) {
        const excludeStates = "AS;FM;GU;MP;PW;VI";
        const searchUrl = `${this.lookupAddressUrl}key=${this.apiKey}&search=${encodeURIComponent(query)}&exclude_states=${encodeURIComponent(excludeStates)}&selected=${selected ? encodeURIComponent(selected) : ""}`;
        fetch(searchUrl)
            .then(response => {
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                if (data.suggestions) {
                    this.displaySuggestions(data.suggestions);
                } else {
                    console.log("no suggestions");
                }
            })
            .catch(error => {
                console.error(error);
            });   
    }

    private selectSuccess(data) {
        this.selectCallback(data, this.searchInput);
    }

    private searchOpen() {
        this.openCallback(this.searchInput);
    }

    onSelect(callback: (data, searchInput) => void): AddressSearch {
        this.selectCallback = callback;
        return this;
    }

    onOpen(callback: (searchInput) => void): AddressSearch {
        this.openCallback = callback;
        return this;
    }
}

export const showInvalidAddressWarning = (invalidAddresses: [], isMulti: boolean): void => {

    const $invalidAddresses = $("#address-verification-overlay .invalid-addresses");
    $invalidAddresses.html("");

    invalidAddresses.forEach((address: { [key: string]: any }) => {
        const shipGroup = $("<div>").attr("class", "ship-grp");

        if (isMulti) {
            const productTitles = document.querySelectorAll(".shipping-address .product-title");
            const productTitle = productTitles[address.index].textContent;
            const $productTitleHeader = $("<h4>").html(productTitle);
            shipGroup.append($productTitleHeader);
        }

        $invalidAddresses.append(shipGroup);

        const { addressLine1, city, state, zip } = address;
        const addressLine2 = (address.addressLine2) ? address.addressLine2 : "";

        const unverifiedAddress = $("<div>", {
            class: "unverified-address",
            html: `
                    <div class='address'>${addressLine1}</div>
                    <div class='address2'>${addressLine2}</div>
                    <div class='cityStateZip'>
                        <span class='city'>${city},</span> <span class='state'>${state}</span> <span class='zip'>${zip}</span>
                    </div>
                `
        });
        shipGroup.append(unverifiedAddress);
    });

    const modalTitle = $('#address-verification-overlay').data("modal-title");
    // @ts-ignore
    M2.Modal.create('address-verification', modalTitle, '#address-verification-overlay', 'modal-sm', true, "static");
}

export const postAddressForValidation = (url: string, data: { [key: string]: any }) => {
    return $.ajax({
        url: url,
        type: 'post',
        dataType: "json",
        data: data,
    });
}


export const getAddressesForValidation = async ($container?: JQuery<HTMLElement>) => {
    // creating an object of obeject to allow for multi-ship index that is non sequential
    const addresses: { [index: number]: any } = {};
    const $addressFieldCollection = $container ? $container.find(".address-fields") : $(".address-fields");

    $addressFieldCollection.each(function (index) {
        const $addressFields = $(this);
        addresses[index] = ({
            AddressLine1: $addressFields.find(".address input[type='text']").val(),
            AddressLine2: $addressFields.find(".address2 input[type='text']").val(),
            City: $addressFields.find(".city input[type='text']").val(),
            State: $addressFields.find(".state select").val(),
            Zip: $addressFields.find(".zip input[type='text']").val(),
        });
    });
    return addresses;
}

export const updateAddresses = ($container: JQuery<HTMLElement>, providedAddresses, callback): void => {
    providedAddresses.forEach(function (address) {
        const index = address.index;
        const { residentialDeliveryIndicator, addressLine1, addressLine2, city, state, zip } = address;
        $container.eq(index).find(".residential-delivery-indicator").val(residentialDeliveryIndicator);
        $container.eq(index).find(".address input").val(addressLine1);
        $container.eq(index).find(".address2 input").val(addressLine2);
        $container.eq(index).find(".city input").val(city);
        $container.eq(index).find(".state select").val(state);
        $container.eq(index).find(".zip input").val(zip);
    });

    if (callback) {
        callback();
    }
}

export const processHiddenErrors = ($providedFields: JQuery<HTMLElement>): void => {
    $providedFields.each(function (index, obj) {
        const $invalidInput = $(obj);
        const $form = $invalidInput.closest("form");
        const $shippingAddressContainer = $(".shipping-address-container");
        $shippingAddressContainer.attr("data-view-mode", "search");
        const $addressSuggestionRequired = $shippingAddressContainer.find(".address-search-required");
        $addressSuggestionRequired.val("True");
        const $searchField = $shippingAddressContainer.find(".address-autocomplete");
        $searchField.val("");
        const $searchResults = $shippingAddressContainer.find(".address-suggestions");
        $searchResults.hide();
        $form.valid();
        return false;
    });
}